import { createContext, useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { ChartOfAccountsStateType } from './types';
import { useMatch } from 'react-router';
import { DEFAULTCHARTOFACCOUNTS } from 'constants/index';
import { ChartOfAccountsDtoV2 } from 'types';
import { useMyCharts } from 'features/charts-of-accounts/api/getMyCharts';
import { useAuth } from './useAuthContext';

const ChartOfAccountsContext = createContext<ChartOfAccountsStateType | undefined>(undefined);

interface ChartOfAccountsProviderProps {
  children: React.ReactNode;
}

export const ChartOfAccountsProvider = ({ children }: ChartOfAccountsProviderProps) => {
  const { getToken } = useAuth();
  const match = useMatch('/chart-of-accounts/:chartOfAccountsId/*');
  const { initialChartOfAccountsId, initialFromUrl } = useMemo(
    () => ({
      initialChartOfAccountsId: match
        ? match.params.chartOfAccountsId
        : (localStorage.getItem(DEFAULTCHARTOFACCOUNTS) ?? undefined),
      initialFromUrl: Boolean(match),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const initialValue = {
    id: initialChartOfAccountsId ? Number(initialChartOfAccountsId) : undefined,
    code: '',
    ref: '',
    name: '',
    status: 1,
    isAccountingMappingEnabled: false,
    datasetSubscriptions: [],
  };

  const [chartOfAccounts, setChartOfAccounts] = useState<ChartOfAccountsDtoV2 | undefined>(initialValue);
  const [initialRender, setInitialRender] = useState(true);

  const { data: myChartOfAccountsList, error } = useMyCharts({ queryConfig: { enabled: Boolean(getToken()) } });

  const updateChartOfAccounts = useCallback(
    (chartId?: number) => {
      const chart = myChartOfAccountsList?.data.find((c) => c.id === chartId);
      if (chart) {
        setChartOfAccounts(chart);
      }
    },
    [myChartOfAccountsList]
  );

  useEffect(() => {
    if (initialRender && myChartOfAccountsList?.data.length) {
      queueMicrotask(() => {
        // A stored default the user can't access (e.g. another user's in this browser) falls back; a url id is kept as is.
        const charts = myChartOfAccountsList.data;
        const initialChart = charts.find((c) => c.id === Number(initialChartOfAccountsId));
        if (initialChart || !initialFromUrl) setChartOfAccounts(initialChart ?? charts[0]);
        setInitialRender(false);
      });
    }
  }, [initialChartOfAccountsId, initialFromUrl, initialRender, myChartOfAccountsList]);

  const contextValue = useMemo(() => {
    return {
      myChartOfAccountsList: myChartOfAccountsList?.data ?? undefined,
      chartOfAccounts,
      updateChartOfAccounts,
      error,
    };
  }, [chartOfAccounts, error, myChartOfAccountsList?.data, updateChartOfAccounts]);

  return <ChartOfAccountsContext.Provider value={contextValue}>{children}</ChartOfAccountsContext.Provider>;
};

export const useChartOfAccounts = () => {
  const context = useContext(ChartOfAccountsContext);
  if (!context) {
    throw new Error('useChartOfAccounts must be used within a ChartOfAccountsProvider');
  }
  return context;
};
